"use client";

import { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useSearchParams } from "next/navigation";

import cn from "classnames";

import { BlueGradientDivider } from "../blueGradientDivider/blueGradientDivider.component";
import { CloseIcon } from "../icons/close.icon";
import { Loader } from "../loader/loader.component";
import {
  QueryLink,
  useRemoveQueryParam,
} from "../queryLink/queryLink.component";
import { Text } from "../text/text.component";

export const DrawerWrapper = ({
  query,
  className,
  title,
  loading,
  showGradientBorder,
  headerVariant = "default",
  children,
  ...rest
}: DrawerProps) => {
  const searchParams = useSearchParams();
  const removeQueryParam = useRemoveQueryParam();
  const drawerRef = useRef<HTMLDialogElement>(null);
  const isOpen = searchParams.has(query);

  const closeDrawer = () => {
    if (!isOpen) return;
    removeQueryParam(query);
  };

  useLayoutEffect(() => {
    const dialog = drawerRef.current;
    if (!dialog) return;

    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      dialog.close();
    };
  }, [isOpen]);
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader loading size="lg" />
        </div>
      );
    }
    return children;
  };
  return createPortal(
    <dialog
      id="modal"
      ref={drawerRef}
      data-testid="modal"
      aria-labelledby="modal"
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault();
        closeDrawer();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) closeDrawer();
      }}
      className={cn(
        "[&::backdrop]:bg-black/30",
        "fixed inset-0 z-50 m-0 box-border hidden h-dvh max-h-dvh w-full max-w-none justify-end overflow-hidden border-0 bg-transparent p-0 open:flex",
      )}
      {...rest}>
      <div
        className={cn(
          "z-10 box-border flex h-full min-h-0 w-[min(100vw,900px)] max-w-225 shrink-0 grow-0 flex-col overflow-hidden bg-white shadow-lg",
          "translate-x-none opacity-100 transition duration-500 starting:translate-x-90 starting:opacity-0",
          { relative: headerVariant === "floating" },
          className,
        )}>
        <header
          className={cn(
            "flex items-center justify-between",
            headerVariant === "default"
              ? "border-greyscale-strength/30 shrink-0 border-b px-6 py-4"
              : "absolute top-0 right-0 z-20 border-0 bg-transparent p-6",
          )}>
          {headerVariant === "default" ? (
            <Text
              id="monitoring-plan-drawer-title"
              variant="h5"
              component="h2"
              className="text-greyscale-headline">
              {loading ? "Loading" : title}
            </Text>
          ) : null}
          <QueryLink
            remove={query}
            aria-label="Close drawer"
            className="text-greyscale-headline hover:bg-greyscale-background flex h-10 w-10 items-center justify-center rounded-full transition-colors">
            <CloseIcon width={24} height={24} />
          </QueryLink>
        </header>
        {showGradientBorder ? <BlueGradientDivider /> : null}
        <div className="flex-1 overflow-auto">{renderContent()}</div>
      </div>
    </dialog>,
    document.body,
  );
};

export const DrawerLoadingContent = () => (
  <div className="flex h-full items-center justify-center">
    <Loader loading size="lg" />
  </div>
);

export type DrawerProps = {
  query: string;
  /** Applied to the sliding panel, not the `<dialog>` element. */
  className?: string;
  title?: string;
  children?: React.ReactNode;
  loading?: boolean;
  showGradientBorder?: boolean;
  headerVariant?: "default" | "floating";
} & Omit<React.ComponentPropsWithoutRef<"dialog">, "className" | "children">;
